import React, { useState, useMemo, useCallback } from "react";
import { Helmet } from "react-helmet";
import {
  Box,
  Card,
  Container,
  Divider,
  FormControl,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import GenericFeedback from "../../Feedback/GenericFeedback";
import Error403 from "../Error/403";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import RateReviewIcon from "@mui/icons-material/RateReview";
import SchoolIcon from "@mui/icons-material/School";

// Lab type mapping configuration
const LAB_TYPE_MAP = {
  left: { key: 'preconfig', label: 'Preconfigured', view: 'left' },
  center: { key: 'custom', label: 'Custom', view: 'center' },
  right: { key: 'hvm', label: 'Hypervised', view: 'right' },
  justify: { key: 'svm', label: 'Shared', view: 'justify' },
  trainer: { key: 'cloud', label: 'Cloud', view: 'trainer' },
  trainer1: { key: 'wise', label: 'Wise', view: 'trainer1' },
  trainer2: { key: 'manual', label: 'Manual', view: 'trainer2' },
  trainer3: { key: 'saas', label: 'SaaS', view: 'trainer3' },
};

const TrainerFeedback = (props) => {
  const [alignment, setAlignment] = useState("left");
  const [searchTerm, setSearchTerm] = useState("");

  // Get current lab type configuration
  const currentLabConfig = useMemo(() => {
    return LAB_TYPE_MAP[alignment] || LAB_TYPE_MAP.left;
  }, [alignment]);

  const handleLabTypeChange = useCallback((event) => {
    const alignmentType = event.target.value;
    setAlignment(alignmentType);
    // Reset search term when lab type changes
    setSearchTerm("");
  }, []);

  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value);
  }, []);

  const handleClearSearch = useCallback(() => {
    setSearchTerm("");
  }, []);

  const labConfigs = useMemo(() => ({
    preconfig: {
      labType: "preconfig",
      apiEndpoint: "/api/pclabquizscore/",
      title: "Preconfigured",
      dataFilter: (item) => item.application !== 'custom',
      allowedStatuses: ["Terminated", "Creating", "Stopping", "Starting", "Started", "Cordoned", "Start Initiated", "Stopped", "Stop Initiated", "Running"],
    },
    custom: {
      labType: "custom",
      apiEndpoint: "/api/customlabquizscore/",
      title: "Custom",
      dataFilter: (item) => item.application === 'custom',
      allowedStatuses: ["Terminated", "Creating", "Stopping", "Starting", "Started", "Cordoned", "Start Initiated", "Stopped", "Stop Initiated", "Running"],
    },
    hvm: {
      labType: "hvm",
      apiEndpoint: "/api/hvmlabquizscore/",
      title: "Hypervised",
      dataFilter: (item) => item.application !== 'custom',
      allowedStatuses: ["Terminated", "Creating", "Stopping", "Starting", "Started", "Cordoned", "Start Initiated", "Stopped", "Stop Initiated", "Running"],
    },
    svm: {
      labType: "svm",
      apiEndpoint: "/api/svmlabquizscore/",
      title: "Shared",
      dataFilter: (item) => item.application !== 'custom',
      allowedStatuses: ["Terminated", "Creating", "Stopping", "Starting", "Started", "Cordoned", "Start Initiated", "Stopped", "Stop Initiated", "Running"],
    },
    cloud: {
      labType: "cloud",
      apiEndpoint: "/api/cloudlabquizscore/",
      title: "Cloud",
      dataFilter: null,
      allowedStatuses: ["Terminated", "Creating", "Stopping", "Starting", "Started", "Cordoned", "Start Initiated", "Stopped", "Stop Initiated", "Running", "Active"],
    },
    wise: {
      labType: "wise",
      apiEndpoint: "/api/wiselabquizscore/",
      title: "Wise",
      dataFilter: (item) => item.application !== 'custom',
      allowedStatuses: ["Terminated", "Creating", "Stopping", "Starting", "Started", "Cordoned", "Start Initiated", "Stopped", "Stop Initiated", "Running"],
    },
    manual: {
      labType: "manual",
      apiEndpoint: "/api/manuallabquizscore/",
      title: "Manual",
      dataFilter: (item) => item.application !== 'custom',
      allowedStatuses: ["Terminated", "Creating", "Stopping", "Starting", "Started", "Cordoned", "Start Initiated", "Stopped", "Stop Initiated", "Running"],
    },
    saas: {
      labType: "saas",
      apiEndpoint: "/api/saslabquizscore/",
      title: "SaaS",
      dataFilter: (item) => item.application !== 'custom',
      allowedStatuses: ["Terminated", "Creating", "Stopping", "Starting", "Started", "Cordoned", "Start Initiated", "Stopped", "Stop Initiated", "Running"],
    },
  }), []);

  const currentConfig = useMemo(() => {
    return labConfigs[currentLabConfig.key] || labConfigs.preconfig;
  }, [currentLabConfig.key, labConfigs]);

  // Check permissions
  const hasPermission = !(
    localStorage.getItem("user_access") === "You do not have permission to perform this action." &&
    localStorage.getItem("group") !== "trainer"
  );

  if (!hasPermission) {
    return <Error403 />;
  }

  return (
    <>
      <Helmet>
        <title>LabAsService - Trainer Feedback</title>
      </Helmet>
      <Container maxWidth={false} sx={{ py: 6, pb: 8, px: { xs: 2, sm: 3, md: 4, lg: 5, xl: 6 } }}>
        <Card {...props} sx={{ mb: 4 }}>
          {/* Hero Header */}
          <Box
            sx={{
              background: 'linear-gradient(135deg, rgba(46,125,50,0.12), rgba(21,101,192,0.12))',
              borderTopLeftRadius: 12,
              borderTopRightRadius: 12,
              p: { xs: 2.5, md: 3 },
            }}
          >
            <Stack direction="row" spacing={2} alignItems="center">
              <RateReviewIcon color="primary" sx={{ fontSize: 32 }} />
              <Box>
                <Typography variant="h4" component="h1">
                  Trainer Feedback
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  View and manage feedback for {currentLabConfig.label.toLowerCase()} labs
                </Typography>
              </Box>
            </Stack>
          </Box>

          <Divider />

          {/* Controls Section */}
          <Box sx={{ px: { xs: 2.5, md: 3 }, py: 2.5 }}>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={2}
              alignItems={{ xs: 'stretch', sm: 'center' }}
              justifyContent="space-between"
              sx={{ mb: 3 }}
            >
              {/* Lab Type Selector */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: { sm: 280 } }}>
                <SchoolIcon color="primary" />
                <Typography
                  sx={{
                    whiteSpace: 'nowrap',
                    fontWeight: 600,
                    fontSize: '1rem',
                    color: 'text.primary',
                  }}
                >
                  Lab Type
                </Typography>
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <Select
                    labelId="labType-label"
                    id="labType"
                    value={alignment}
                    onChange={handleLabTypeChange}
                    sx={{
                      backgroundColor: 'white',
                      '& .MuiSelect-select': {
                        py: 1,
                      },
                    }}
                  >
                    {Object.entries(LAB_TYPE_MAP).map(([value, config]) => (
                      <MenuItem key={value} value={value}>
                        {config.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {/* Search Field */}
              <TextField
                placeholder="Search Lab Name"
                value={searchTerm}
                onChange={handleSearchChange}
                size="small"
                sx={{
                  width: '100%',
                  maxWidth: { xs: '100%', sm: 400, md: 500 },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: searchTerm && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={handleClearSearch}
                        aria-label="clear search"
                      >
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Stack>

            {/* GenericFeedback Component */}
            <Box sx={{ pt: 0 }}>
              <GenericFeedback 
                config={currentConfig} 
                searchTerm={searchTerm} 
                setSearchTerm={setSearchTerm} 
              />
            </Box>
          </Box>
        </Card>
      </Container>
    </>
  );
};

export default TrainerFeedback;
